import { useEffect, useMemo, useRef } from 'react';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';

export type Options = {
  otherKeysInCombinationAllowed?: boolean;
  ignoreInputFields?: boolean;
  capture?: boolean;
};

export const useKeysDown = (
  keys: string[] | string[][],
  callback: (e: KeyboardEvent, keys: string[]) => void,
  { otherKeysInCombinationAllowed, ignoreInputFields, capture }: Options = {
    otherKeysInCombinationAllowed: false,
    ignoreInputFields: false,
    capture: false,
  },
): void => {
  const commandPressed = useRef(false);
  const pressed = useRef<string[]>([]);
  const keysToProcess: string[][] = useMemo(
    () =>
      Array.isArray(keys[0]) ? (keys as string[][]) : ([keys] as string[][]),
    [keys],
  );

  useEffect(() => {
    const isMacOs = getIsMacOs();
    const commandKeys = isMacOs
      ? ['MetaLeft', 'MetaRight']
      : ['ControlLeft', 'ControlRight'];

    const onKeyDown = (e: KeyboardEvent) => {
      if (ignoreInputFields) {
        const activeElement = document.activeElement;
        const tagName = activeElement?.tagName;
        const isInput = tagName === 'INPUT' || tagName === 'TEXTAREA';
        const isContentEditable = (activeElement as HTMLElement)
          ?.isContentEditable;

        if (isInput || isContentEditable) {
          const type = (activeElement as HTMLInputElement).type;
          const allowedTypes = [
            'checkbox',
            'radio',
            'button',
            'submit',
            'reset',
          ];

          // allow shortcuts for these types
          if (!(isInput && allowedTypes.includes(type))) {
            return;
          }
        }
      }

      const keyId = e.code || e.key;

      if (commandKeys.includes(keyId)) {
        commandPressed.current = true;
      }

      // figure out if the pressed key is one we track
      const isTrackedKey = keysToProcess.some((keys) => keys.includes(keyId));

      // if the key is not tracked, or it is already tracked part of the pressed keys
      if (
        commandPressed.current ||
        (otherKeysInCombinationAllowed && !isTrackedKey) ||
        pressed.current.includes(keyId)
      ) {
        return;
      }

      pressed.current.push(keyId);

      const keysPressed = keysToProcess.map((combinedKeys) =>
        combinedKeys.map((key) => pressed.current.includes(key)),
      );

      keysPressed.forEach((pressedKeys, index) => {
        if (
          pressedKeys.every(Boolean) &&
          pressedKeys.length === pressed.current.length
        ) {
          callback(e, keysToProcess[index]);
        }
      });
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const keyId = e.code || e.key;

      if (commandKeys.includes(keyId)) {
        commandPressed.current = false;
      }

      pressed.current = pressed.current.filter((code) => code !== keyId);
    };
    const unregisterKeys = () => {
      pressed.current = [];
      commandPressed.current = false;
    };

    document.addEventListener('keydown', onKeyDown, capture);
    document.addEventListener('keyup', onKeyUp, capture);
    window.addEventListener('blur', unregisterKeys);

    return () => {
      document.removeEventListener('keydown', onKeyDown, capture);
      document.removeEventListener('keyup', onKeyUp, capture);
      document.removeEventListener('blur', unregisterKeys);
    };
  }, [
    callback,
    capture,
    ignoreInputFields,
    keys,
    keysToProcess,
    otherKeysInCombinationAllowed,
  ]);
};
